/**
 * PF v2 — session file resource wire contract.
 *
 * Mirrors the backend `SessionFileV2DTO.kt` surface (backend-platform#174):
 * two resources under `/workspace/{workspaceId}/sessions/{sessionId}`, eight
 * operations, keyed on the catalogue's `productCode` instead of the legacy numeric
 * product id the v1 `order/{sessionId}` endpoints require.
 *
 * Why this exists at all: the v1 mints take a `productId: Long` the v2 catalogue
 * never emits, so the FE derived one by parsing `p_<digits>` out of `productCode`.
 * ISSUE-2944 re-keys product codes onto real Salesforce codes, which makes that
 * parse return null for every product and errors every dropzone. These endpoints
 * take the code verbatim and resolve it server-side through the same path order
 * placement uses, so **both code forms resolve** — the FE can adopt now, while the
 * catalogue still emits `p_<id>`, and nothing breaks when 2944 flips the values.
 *
 * Two shape notes that differ from v1 and are easy to get wrong:
 *   - **Visual vs document is a `kind` field, not an endpoint split.** v1 had a
 *     separate endpoint family per resource type; v2 derives the type from `kind`.
 *   - **Order-level documents have their own resource.** v1 reached them by
 *     overloading the `ORDER_SUPPLEMENTARY_DOCUMENT` document *tag* on the
 *     product endpoint; v2 rejects that tag on `/files` and serves them from
 *     `/order-files`, keyed by type.
 */

import type { UploadTagType } from 'constants/documents'

/** Whether a session file is an image or a document — the only resource-type input v2 takes. */
export enum SessionFileKind {
  VISUAL = 'VISUAL',
  DOCUMENT = 'DOCUMENT',
}

/**
 * Which order-level document slot a file occupies.
 *
 * Cardinality differs per slot, and it is the slot's **declared** cardinality that
 * callers branch on, never the number of documents currently present:
 * `POWER_OF_ATTORNEY` and `POWER_OF_ATTORNEY_REPRESENTATIVES` hold one document each
 * and reject a second upload with 409 until the first is deleted, while
 * `SUPPLEMENTARY` holds several (the legacy PoA "other" zone accepted 0..15).
 *
 * Consequence for deletes: a `SUPPLEMENTARY` document must be addressed by
 * slot + filename. Addressing that slot by type alone is refused with 409 rather
 * than deleting an arbitrary member of it.
 */
export enum SessionOrderDocumentType {
  POWER_OF_ATTORNEY = 'POWER_OF_ATTORNEY',
  POWER_OF_ATTORNEY_REPRESENTATIVES = 'POWER_OF_ATTORNEY_REPRESENTATIVES',
  SUPPLEMENTARY = 'SUPPLEMENTARY',
}

/** Body of `POST …/files/upload-url` — a file attached to one product line. */
export interface SessionFileUploadV2DTO {
  /**
   * Product code of the line this file belongs to, copied verbatim from the
   * catalogue response. Send it exactly as received — never parse or construct it.
   */
  productCode: string
  kind: SessionFileKind
  /** The file's name as it is on the client's disk. Must carry a usable extension (400 otherwise). */
  filename: string
  contentType: string
  /**
   * What a document depicts. Documents only — sending it for a `VISUAL` is
   * rejected rather than ignored, and `ORDER_SUPPLEMENTARY_DOCUMENT` is rejected
   * outright (that file belongs on `/order-files`).
   */
  tag?: UploadTagType
}

/**
 * Body of `PUT …/files/upload-url` — replace the bytes of an already-uploaded file.
 *
 * Carries no product reference: the file is already bound to its line. `filename`
 * must be the SERVER-assigned name the upload response returned, echoed verbatim
 * — the stored blob is named by the backend (`<sessionId>-backbonephoto-001.pdf`)
 * and the client's original name lives only in blob metadata.
 */
export interface SessionFileReuploadV2DTO {
  filename: string
  contentType: string
}

/** One file uploaded against a product line. */
export interface SessionFileV2DTO {
  /** Server-assigned filename — the key for replace, delete and download. */
  filename: string
  kind: SessionFileKind
  /** What the document depicts, when one was given. Always absent for a visual. */
  tag?: UploadTagType
}

/**
 * Response of `GET …/files` — visuals AND documents together, one entry per
 * product line that has at least one file. Keys are the same opaque product codes
 * the catalogue emits, so matching a file to its catalogue entry is string
 * equality with no parsing.
 */
export interface SessionFilesV2DTO {
  files: Record<string, SessionFileV2DTO[]>
}

/** Body of `POST …/order-files/upload-url` — a document belonging to the order as a whole. */
export interface OrderDocumentUploadV2DTO {
  type: SessionOrderDocumentType
  filename: string
  contentType: string
}

/** One occupied order-level document slot. */
export interface OrderDocumentV2DTO {
  type: SessionOrderDocumentType
  filename: string
}

/** Response of `GET …/order-files`. Empty slots are absent, not null entries. */
export interface OrderDocumentsV2DTO {
  documents: OrderDocumentV2DTO[]
}
